import { useCallback, useState } from 'react';
import { Banner, BlockStack, Box, Button, Card, Page, Text } from '@shopify/polaris';

import { publicApi } from '@/api/client';
import { explain, getAssertion, isSupported } from '@/auth/passkey';
import { setAdmin, setSession } from '@/auth/session';

/**
 * Sign in. Registration lives nowhere on this page any more — see the note
 * where the form used to be, below.
 *
 * ── Login asks for no username ─────────────────────────────────────────────
 *
 * The passkey is discoverable: it carries the user handle, so the browser
 * offers the right credential and the server learns who it is from the
 * assertion. That also means this screen leaks nothing — there is no field in
 * which to discover whether an account exists.
 *
 * ── Registration is behind a token you paste in ────────────────────────────
 *
 * ⚠ Deliberately not a link, not an invite email, not a flow. The Worker
 * refuses registration unless the owner has turned it on AND supplied a secret,
 * so the form asks for that secret directly. It is meant to be used once, with
 * the switch turned back off afterwards.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported = isSupported();

  const signIn = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const begin = await publicApi<{
        options: { publicKey: Parameters<typeof getAssertion>[0] };
        sessionId: string;
      }>('/ops/auth/login/begin', { method: 'POST' });

      const assertion = await getAssertion(begin.options.publicKey);

      const done = await publicApi<{
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
        admin: { id: number; username: string; displayName: string | null };
      }>(`/ops/auth/login/finish?sessionId=${encodeURIComponent(begin.sessionId)}`, {
        method: 'POST',
        body: JSON.stringify(assertion),
      });

      setSession(done.accessToken, done.expiresIn, done.refreshToken);
      setAdmin(done.admin);
      onSignedIn();
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(false);
    }
  }, [onSignedIn]);

  return (
    <Page narrowWidth>
      <Box paddingBlockStart="1200">
        <Card>
          <BlockStack gap="500">
            <BlockStack gap="200">
              <Text as="h1" variant="headingLg">
                Confetto Ops
              </Text>
              <Text as="p" tone="subdued">
                Internal. Sign in with the passkey on this device.
              </Text>
            </BlockStack>

            {!supported && (
              <Banner tone="critical" title="This browser cannot use passkeys">
                <p>
                  Ops sign-in needs WebAuthn. Use a current version of Chrome, Safari, Edge or
                  Firefox — there is no password to fall back on, by design.
                </p>
              </Banner>
            )}

            {error && (
              <Banner tone="critical" title="Sign-in failed" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}

            <Button
              variant="primary"
              size="large"
              fullWidth
              loading={busy}
              disabled={!supported}
              onClick={() => void signIn()}
            >
              Sign in with a passkey
            </Button>

            {/*
              ⚠ THE REGISTER FORM IS GONE, DELIBERATELY.
              Both admins exist, and a public "create an admin" form on the sign-in
              page of a dashboard that reads every merchant's data is surface with
              no remaining purpose. The Worker refuses it anyway —
              OPS_REGISTER_ENABLED is "false" on BOTH environments — so this is the
              second lock, not the only one.

              ⚠ This does NOT remove the second-device path, which matters because
              there is no password reset here: an admin with one passkey on one
              device is one lost laptop away from permanent lockout. Adding a
              device lives on the Profile page, behind an authenticated session,
              on /ops/api/passkey/register/* — a different endpoint that this flag
              does not gate.

              To enrol a genuinely new admin: flip OPS_REGISTER_ENABLED to "true",
              deploy, restore this form, register, then put both back. `preflight`
              blocks a production deploy that leaves the flag on.
            */}
          </BlockStack>
        </Card>
      </Box>
    </Page>
  );
}
